import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { syllabusApi } from "../config/syllabusApi";
import Toast from "../Toast/Toast";
import "./CurrentAffairsQuizAdmin.css";

const defaultPayload = {
  exam_code: "SSC_CGL",
  exam_name: "SSC CGL",
  conducting_body: "Staff Selection Commission",
  notification_year: 2026,
  level: ["graduate"],
  exam_pattern: {
    mode: "online",
    question_type: "MCQ",
    duration_minutes: 60,
    total_questions: 100,
    total_marks: 200,
    mark_per_question: 2,
    negative_marking: true,
    negative_mark_per_question: 0.5,
  },
  papers: [
    {
      paper_code: "PAPER_1",
      paper_name: "Tier 1",
      subjects: [
        {
          subject_name: "Quantitative Aptitude",
          questions: 25,
          marks: 50,
          topics: ["Number System", "Algebra", "Geometry", "Trigonometry"],
        },
      ],
    },
  ],
};

const formatDate = (value) => String(value || "").trim() || "NA";
const formatValue = (value) => (value == null || value === "" ? "NA" : String(value));

const parsePayloadOrThrow = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Content must be valid JSON.");
  }
};

const pickFirstValue = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value != null && value !== "") return value;
  }
  return "";
};

const toFiniteNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (value.trim().toLowerCase() === "true") return true;
    if (value.trim().toLowerCase() === "false") return false;
  }
  return undefined;
};

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || "").trim()).filter(Boolean);
};

const hasNonEmptyOptions = (value) => {
  if (Array.isArray(value)) return value.some((item) => String(item || "").trim());
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return Boolean(String(value || "").trim());
};

const normalizeSubject = (subject) => {
  const base = subject && typeof subject === "object" && !Array.isArray(subject) ? subject : {};
  const { subjectName, ...rest } = base;

  return {
    ...rest,
    subject_name: pickFirstValue(base?.subject_name, subjectName),
    questions: toFiniteNumber(base?.questions),
    marks: toFiniteNumber(base?.marks),
    topics: normalizeStringArray(base?.topics),
  };
};

const normalizePaper = (paper) => {
  const base = paper && typeof paper === "object" && !Array.isArray(paper) ? paper : {};
  const { paperCode, paperName, ...rest } = base;

  return {
    ...rest,
    paper_code: pickFirstValue(base?.paper_code, paperCode),
    paper_name: pickFirstValue(base?.paper_name, paperName),
    subjects: Array.isArray(base?.subjects) ? base.subjects.map(normalizeSubject) : [],
  };
};

const normalizeExamPattern = (examPattern) => {
  const base =
    examPattern && typeof examPattern === "object" && !Array.isArray(examPattern) ? examPattern : {};
  const { questionType, durationMinutes, totalQuestions, totalMarks, markPerQuestion, negativeMarking, negativeMarkPerQuestion, ...rest } =
    base;

  return {
    ...rest,
    mode: pickFirstValue(base?.mode),
    question_type: pickFirstValue(base?.question_type, questionType),
    duration_minutes: toFiniteNumber(pickFirstValue(base?.duration_minutes, durationMinutes)),
    total_questions: toFiniteNumber(pickFirstValue(base?.total_questions, totalQuestions)),
    total_marks: toFiniteNumber(pickFirstValue(base?.total_marks, totalMarks)),
    mark_per_question: toFiniteNumber(pickFirstValue(base?.mark_per_question, markPerQuestion)),
    negative_marking: toBoolean(pickFirstValue(base?.negative_marking, negativeMarking)),
    negative_mark_per_question: toFiniteNumber(
      pickFirstValue(base?.negative_mark_per_question, negativeMarkPerQuestion)
    ),
  };
};

const buildAdminPayload = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Syllabus payload must be a JSON object.");
  }

  const {
    examName,
    examCode,
    conductingBody,
    notificationYear,
    examPattern,
    ...rest
  } = payload;

  return {
    ...rest,
    exam_code: pickFirstValue(payload?.exam_code, examCode),
    exam_name: pickFirstValue(payload?.exam_name, examName),
    conducting_body: pickFirstValue(payload?.conducting_body, conductingBody),
    notification_year: toFiniteNumber(
      pickFirstValue(payload?.notification_year, notificationYear)
    ),
    level: normalizeStringArray(payload?.level),
    exam_pattern: normalizeExamPattern(payload?.exam_pattern || examPattern),
    papers: Array.isArray(payload?.papers) ? payload.papers.map(normalizePaper) : [],
  };
};

const validateSyllabusPayload = (payload) => {
  const normalizedPayload = buildAdminPayload(payload);

  if (!String(normalizedPayload?.exam_name || "").trim()) {
    throw new Error("exam_name is required.");
  }

  const examPattern = normalizedPayload?.exam_pattern;
  if (!examPattern || typeof examPattern !== "object" || Array.isArray(examPattern)) {
    throw new Error("exam_pattern is required.");
  }

  if (toFiniteNumber(examPattern?.total_questions) == null) {
    throw new Error("exam_pattern.total_questions is required.");
  }

  if (toFiniteNumber(examPattern?.total_marks) == null) {
    throw new Error("exam_pattern.total_marks is required.");
  }

  if (toFiniteNumber(examPattern?.mark_per_question) == null) {
    throw new Error("exam_pattern.mark_per_question is required.");
  }

  if (toFiniteNumber(examPattern?.duration_minutes) == null) {
    throw new Error("exam_pattern.duration_minutes is required.");
  }

  if (typeof examPattern?.negative_marking !== "boolean") {
    throw new Error("exam_pattern.negative_marking is required.");
  }

  if (
    examPattern?.negative_marking &&
    toFiniteNumber(examPattern?.negative_mark_per_question) == null
  ) {
    throw new Error("exam_pattern.negative_mark_per_question is required when negative_marking=true.");
  }

  if (!Array.isArray(normalizedPayload?.papers) || !normalizedPayload.papers.length) {
    throw new Error("papers must contain at least one paper.");
  }

  normalizedPayload.papers.forEach((paper, paperIndex) => {
    if (!Array.isArray(paper?.subjects) || !paper.subjects.length) {
      throw new Error(`papers[${paperIndex}].subjects must contain at least one subject.`);
    }

    paper.subjects.forEach((subject, subjectIndex) => {
      if (!String(subject?.subject_name || "").trim()) {
        throw new Error(`papers[${paperIndex}].subjects[${subjectIndex}].subject_name is required.`);
      }

      const hasTopics = Array.isArray(subject?.topics) && subject.topics.length > 0;
      const hasOptions = hasNonEmptyOptions(subject?.options);
      if (!hasTopics && !hasOptions) {
        throw new Error(
          `papers[${paperIndex}].subjects[${subjectIndex}] must include non-empty topics or options.`
        );
      }
    });
  });

  return normalizedPayload;
};

const getErrorMessage = (error, fallback) => {
  if (Number(error?.status) === 403) return "Not authorized.";
  return error?.message || fallback;
};

const TEST_SERIES_FORMATS = [
  {
    key: "FULL_TEST",
    shortLabel: "Full",
    generatedKey: "generatedFullTestCount",
    additionalKey: "additionalGeneratableFullTestCount",
    totalKey: "totalGeneratableFullTestCount",
    createdIdsKey: "createdFullTestQuizIds",
  },
  {
    key: "SECTIONAL_TEST",
    shortLabel: "Sectional",
    generatedKey: "generatedSectionalTestCount",
    additionalKey: "additionalGeneratableSectionalTestCount",
    totalKey: "totalGeneratableSectionalTestCount",
    createdIdsKey: "createdSectionalTestQuizIds",
  },
  {
    key: "PYQ_TEST",
    shortLabel: "PYQ",
    generatedKey: "generatedPyqTestCount",
    additionalKey: "additionalGeneratablePyqTestCount",
    totalKey: "totalGeneratablePyqTestCount",
    createdIdsKey: "createdPyqTestQuizIds",
  },
];

const readCountValue = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const normalizeOverviewRow = (row) => {
  const base = row && typeof row === "object" && !Array.isArray(row) ? row : {};
  const generatedSeriesCount = readCountValue(
    base?.generatedSeriesCount,
    TEST_SERIES_FORMATS.reduce((sum, format) => sum + readCountValue(base?.[format.generatedKey]), 0)
  );
  const additionalGeneratableSeriesCount = readCountValue(
    base?.additionalGeneratableSeriesCount,
    TEST_SERIES_FORMATS.reduce((sum, format) => sum + readCountValue(base?.[format.additionalKey]), 0)
  );
  const totalGeneratableSeriesCount = readCountValue(
    base?.totalGeneratableSeriesCount,
    TEST_SERIES_FORMATS.reduce((sum, format) => sum + readCountValue(base?.[format.totalKey]), 0)
  );

  return {
    ...base,
    generatedSeriesCount,
    additionalGeneratableSeriesCount,
    totalGeneratableSeriesCount,
  };
};

const normalizeOverviewRows = (payload) => {
  if (Array.isArray(payload)) return payload.map(normalizeOverviewRow);
  if (Array.isArray(payload?.items)) return payload.items.map(normalizeOverviewRow);
  if (Array.isArray(payload?.overview)) return payload.overview.map(normalizeOverviewRow);
  if (Array.isArray(payload?.data)) return payload.data.map(normalizeOverviewRow);
  return [];
};

const readExamKey = (item) =>
  String(item?.examKey || item?.examCode || item?.exam_code || item?.key || "").trim();

const readExamName = (item) =>
  String(item?.examName || item?.exam_name || item?.title || "").trim();

const readPreviewExamPattern = (item) => item?.exam_pattern || item?.examPattern || {};

const renderFormatStats = (row, valueKey) => (
  <div className="ca-admin-current-filter">
    {TEST_SERIES_FORMATS.map((format) => (
      <span key={`${format.key}-${valueKey}`}>
        {format.shortLabel}: <strong>{formatValue(row?.[format[valueKey]])}</strong>
      </span>
    ))}
  </div>
);

const renderCreatedIdsByFormat = (result) => (
  <div className="ca-admin-current-filter" style={{ marginTop: 8 }}>
    {TEST_SERIES_FORMATS.map((format) => {
      const ids = Array.isArray(result?.[format.createdIdsKey]) ? result[format.createdIdsKey] : [];
      return (
        <span key={format.createdIdsKey}>
          {format.shortLabel} IDs: <strong>{ids.length ? ids.join(", ") : "NA"}</strong>
        </span>
      );
    })}
  </div>
);

export function AdminSyllabusListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const loadList = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await syllabusApi.adminList();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load syllabus."));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const handleDelete = async (id) => {
    setDeletingId(id);
    setError("");
    setMessage("");
    try {
      await syllabusApi.adminDelete(id);
      setMessage("Syllabus deleted successfully.");
      setToastMessage("Syllabus deleted successfully.");
      await loadList();
    } catch (err) {
      const nextError = getErrorMessage(err, "Unable to delete syllabus.");
      setError(nextError);
      setToastMessage(nextError);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="ca-admin-page">
      <div className="ca-admin-header">
        <h1>Admin Syllabus Manager</h1>
        <div className="ca-admin-actions">
          <button type="button" className="ca-admin-btn ca-admin-btn-primary" onClick={() => navigate("/admin/syllabus/new")}>
            New Syllabus
          </button>
          <button type="button" className="ca-admin-btn ca-admin-btn-primary" onClick={() => navigate("/admin/syllabus/test-series")}>
            Test Series Control
          </button>
          <button
            type="button"
            className="ca-admin-btn ca-admin-btn-primary"
            onClick={() => navigate("/admin/syllabus/automation")}
          >
            Syllabus Automation
          </button>
          <button
            type="button"
            className="ca-admin-btn ca-admin-btn-primary"
            onClick={() => navigate("/admin/syllabus/question-bank")}
          >
            Question Bank Flow
          </button>
          <button
            type="button"
            className="ca-admin-btn ca-admin-btn-primary"
            onClick={() => navigate("/admin/analytics")}
          >
            Analytics
          </button>
          <button type="button" className="ca-admin-btn" onClick={() => navigate("/admin/dashboard/quiz")}>
            Back To Dashboard
          </button>
          <button type="button" className="ca-admin-btn" onClick={loadList} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {message ? <p className="ca-admin-msg ok">{message}</p> : null}
      {error ? <p className="ca-admin-msg err">{error}</p> : null}

      <div className="ca-admin-list-card">
        <h2>All Syllabus</h2>
        {loading ? <p>Loading syllabus...</p> : null}
        {!loading && !items.length ? <p>No syllabus found.</p> : null}
        {!loading && items.length ? (
          <div className="ca-admin-table-wrap">
            <table className="ca-admin-table">
              <thead>
                <tr>
                  <th>Exam Name</th>
                  <th>Exam Code</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={`${item?.id || item?.slugurl || "syllabus"}-${index}`}>
                    <td data-label="Exam Name">{readExamName(item) || "Untitled Syllabus"}</td>
                    <td data-label="Exam Code">{readExamKey(item) || "NA"}</td>
                    <td data-label="Updated At">{formatDate(item?.updatedAt)}</td>
                    <td data-label="Actions">
                      <div className="ca-admin-actions">
                        <Link className="ca-admin-btn" to={`/syllabus/${item?.slugurl || item?.slugWithId}`}>
                          View
                        </Link>
                        {readExamKey(item) ? (
                          <Link className="ca-admin-btn" to={`/syllabus/exam/${encodeURIComponent(readExamKey(item))}`}>
                            Exam View
                          </Link>
                        ) : null}
                        <button
                          type="button"
                          className="ca-admin-btn"
                          onClick={() => navigate(`/admin/syllabus/${item?.id}/edit`)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ca-admin-btn ca-admin-btn-primary"
                          disabled={!readExamKey(item)}
                          onClick={() =>
                            navigate(
                              `/admin/syllabus/automation?examKey=${encodeURIComponent(
                                readExamKey(item)
                              )}`
                            )
                          }
                        >
                          Automate
                        </button>
                        <button
                          type="button"
                          className="ca-admin-btn ca-admin-btn-danger"
                          disabled={deletingId === item?.id}
                          onClick={() => handleDelete(item?.id)}
                        >
                          {deletingId === item?.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
      <Toast message={toastMessage} onClose={() => setToastMessage("")} />
    </div>
  );
}

export function AdminSyllabusEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [contentText, setContentText] = useState(JSON.stringify(defaultPayload, null, 2));

  useEffect(() => {
    if (!isEditMode) return;

    let mounted = true;

    const loadDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await syllabusApi.adminGetById(id);
        if (!mounted) return;
        setContentText(JSON.stringify(data, null, 2));
      } catch (err) {
        if (!mounted) return;
        setError(getErrorMessage(err, "Unable to load syllabus."));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadDetail();
    return () => {
      mounted = false;
    };
  }, [id, isEditMode]);

  const parsedPreview = useMemo(() => {
    try {
      return JSON.parse(contentText);
    } catch {
      return null;
    }
  }, [contentText]);

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payload = parsePayloadOrThrow(contentText);
      const normalizedPayload = validateSyllabusPayload(payload);

      if (isEditMode) {
        await syllabusApi.adminUpdate(id, normalizedPayload);
        setMessage("Syllabus updated successfully.");
        setToastMessage("Syllabus updated successfully.");
      } else {
        await syllabusApi.adminCreate(normalizedPayload);
        setMessage("Syllabus created successfully.");
        setToastMessage("Syllabus created successfully.");
      }

      navigate("/admin/syllabus");
    } catch (err) {
      const nextError = getErrorMessage(err, "Unable to save syllabus.");
      setError(nextError);
      setToastMessage(nextError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ca-admin-page">
      <div className="ca-admin-header">
        <h1>{isEditMode ? "Edit Syllabus" : "Create Syllabus"}</h1>
        <div className="ca-admin-actions">
          <button type="button" className="ca-admin-btn" onClick={() => navigate("/admin/syllabus")}>
            Back To List
          </button>
          <button
            type="button"
            className="ca-admin-btn ca-admin-btn-primary"
            disabled={saving}
            onClick={handleSubmit}
          >
            {saving ? "Saving..." : isEditMode ? "Update Syllabus" : "Create Syllabus"}
          </button>
        </div>
      </div>

      {message ? <p className="ca-admin-msg ok">{message}</p> : null}
      {error ? <p className="ca-admin-msg err">{error}</p> : null}

      <div className="ca-admin-grid">
        <section className="ca-admin-form-card">
          <h2>JSON Editor</h2>
          <p className="ca-admin-tool-copy">
            Edit the full syllabus payload here using the admin API snake_case format. Required:
            <strong> exam_name</strong>, <strong> exam_pattern.total_questions</strong>,
            <strong> exam_pattern.total_marks</strong>, <strong> exam_pattern.mark_per_question</strong>,
            <strong> exam_pattern.duration_minutes</strong>, <strong> exam_pattern.negative_marking</strong>,
            <strong> papers</strong>, <strong> paper.subjects</strong>, <strong> subject.subject_name</strong>,
            and non-empty <strong> topics</strong> or <strong> options</strong>.
          </p>
          {loading ? <p>Loading syllabus...</p> : null}
          {!loading ? (
            <>
              <label htmlFor="admin-syllabus-json">Syllabus JSON</label>
              <textarea
                id="admin-syllabus-json"
                rows={28}
                spellCheck={false}
                value={contentText}
                onChange={(event) => setContentText(event.target.value)}
              />
            </>
          ) : null}
        </section>

        <section className="ca-admin-list-card">
          <h2>Quick Preview</h2>
          {parsedPreview ? (
            <div className="ca-admin-stats-grid">
              <div className="ca-admin-stat-card">
                <span>Exam Name</span>
                <strong>{String(readExamName(parsedPreview) || "NA")}</strong>
              </div>
              <div className="ca-admin-stat-card">
                <span>Exam Code</span>
                <strong>{String(readExamKey(parsedPreview) || "NA")}</strong>
              </div>
              <div className="ca-admin-stat-card">
                <span>Duration</span>
                <strong>
                  {String(
                    readPreviewExamPattern(parsedPreview)?.duration_minutes ||
                      readPreviewExamPattern(parsedPreview)?.durationMinutes ||
                      "NA"
                  )}
                </strong>
              </div>
              <div className="ca-admin-stat-card">
                <span>Papers</span>
                <strong>{Array.isArray(parsedPreview?.papers) ? parsedPreview.papers.length : 0}</strong>
              </div>
            </div>
          ) : (
            <p className="ca-admin-msg err">Preview unavailable until the JSON is valid.</p>
          )}
        </section>
      </div>
      <Toast message={toastMessage} onClose={() => setToastMessage("")} />
    </div>
  );
}

export function AdminSyllabusTestSeriesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [activeGenerateKey, setActiveGenerateKey] = useState("");
  const [resultsByKey, setResultsByKey] = useState({});

  const loadOverview = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await syllabusApi.adminTestSeriesOverview();
      setRows(normalizeOverviewRows(data));
    } catch (err) {
      setRows([]);
      setError(getErrorMessage(err, "Unable to load test series overview."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const refreshExamRow = async (examKey) => {
    const data = await syllabusApi.adminTestSeriesOverviewByExamKey(examKey);
    const nextRow = normalizeOverviewRows(data)[0];
    if (!nextRow || typeof nextRow !== "object") return;
    setRows((currentRows) => {
      const found = currentRows.some((row) => readExamKey(row) === examKey);
      if (!found) return currentRows;
      return currentRows.map((row) => (readExamKey(row) === examKey ? nextRow : row));
    });
  };

  const handleGenerate = async (examKey) => {
    setActiveGenerateKey(examKey);
    setError("");
    try {
      const result = await syllabusApi.adminGenerateTestSeries(examKey);
      setResultsByKey((current) => ({ ...current, [examKey]: result }));
      setToastMessage(result?.message || "Test series generated successfully.");
      await refreshExamRow(examKey);
    } catch (err) {
      const nextError = getErrorMessage(err, "Unable to generate test series.");
      setError(nextError);
      setToastMessage(nextError);
    } finally {
      setActiveGenerateKey("");
    }
  };

  return (
    <div className="ca-admin-page">
      <div className="ca-admin-header">
        <h1>Test Series Control</h1>
        <div className="ca-admin-actions">
          <button type="button" className="ca-admin-btn" onClick={() => navigate("/admin/syllabus")}>
            Back To Syllabus
          </button>
          <button type="button" className="ca-admin-btn" onClick={loadOverview} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {error ? <p className="ca-admin-msg err">{error}</p> : null}

      <div className="ca-admin-list-card">
        <h2>Exam Overview</h2>
        {loading ? <p>Loading test series overview...</p> : null}
        {!loading && !rows.length ? <p>No exam overview available.</p> : null}
        {!loading && rows.length ? (
          <div className="ca-admin-table-wrap">
            <table className="ca-admin-table">
              <thead>
                <tr>
                  <th>Exam</th>
                  <th>Generated</th>
                  <th>Total Possible</th>
                  <th>Additional Possible</th>
                  <th>Questions Per Series</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const examKey = readExamKey(row);
                  const rowResult = resultsByKey[examKey];
                  return (
                    <tr key={`${examKey || row?.examName || 'exam'}-${index}`}>
                      <td data-label="Exam">
                        <strong>{row?.examName || examKey || "Untitled Exam"}</strong>
                        <div>{formatValue(examKey)}</div>
                      </td>
                      <td data-label="Generated">
                        <strong>{formatValue(row?.generatedSeriesCount)}</strong>
                        {renderFormatStats(row, "generatedKey")}
                      </td>
                      <td data-label="Total Possible">
                        <strong>{formatValue(row?.totalGeneratableSeriesCount)}</strong>
                        {renderFormatStats(row, "totalKey")}
                      </td>
                      <td data-label="Additional Possible">
                        <strong>{formatValue(row?.additionalGeneratableSeriesCount)}</strong>
                        {renderFormatStats(row, "additionalKey")}
                      </td>
                      <td data-label="Questions Per Series">{formatValue(row?.questionsPerSeries)}</td>
                      <td data-label="Actions">
                        <div className="ca-admin-actions">
                          <button
                            type="button"
                            className="ca-admin-btn ca-admin-btn-primary"
                            disabled={!examKey || activeGenerateKey === examKey}
                            onClick={() => handleGenerate(examKey)}
                          >
                            {activeGenerateKey === examKey ? "Generating..." : "Generate"}
                          </button>
                          {examKey ? (
                            <Link className="ca-admin-btn" to={`/syllabus/exam/${encodeURIComponent(examKey)}/test-series-blueprint`}>
                              Blueprint
                            </Link>
                          ) : null}
                        </div>
                        {rowResult ? (
                          <div className="ca-admin-current-filter" style={{ marginTop: 8 }}>
                            <span>Generated now:</span> <strong>{formatValue(rowResult?.generatedNow)}</strong>{" "}
                            <span>Total after run:</span> <strong>{formatValue(rowResult?.generatedTotalAfterRun)}</strong>{" "}
                            <span>Additional left:</span> <strong>{formatValue(rowResult?.additionalGeneratableAfterRun)}</strong>{" "}
                            <span>Created IDs:</span> <strong>{Array.isArray(rowResult?.createdQuizIds) && rowResult.createdQuizIds.length ? rowResult.createdQuizIds.join(", ") : "NA"}</strong>
                          </div>
                        ) : null}
                        {rowResult ? renderFormatStats(rowResult, "generatedKey") : null}
                        {rowResult ? renderCreatedIdsByFormat(rowResult) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
      <Toast message={toastMessage} onClose={() => setToastMessage("")} />
    </div>
  );
}
